import { useState } from 'react'
import { useFetchOrderNotes } from '@/hooks/features/order-note'
import { OrderModel } from '@/http/models/Order/Order'
import { OrderNoteModel as OrderNoteApi } from '@/http/models/OrderNote'
import { toast } from 'sonner'
import { OrderNoteModel } from '@/types'
import { SearchOrCreateCombobox } from './SearchOrCreateCombobox'

interface OrderNoteSelectProps {
    orderId: string
    currentNoteId: string | null
    /** The order's expanded note text, for when it isn't in the first page of notes. */
    currentLabel?: string
    onUpdate?: () => void
    notes?: OrderNoteModel.OrderNote[]
}

export const OrderNoteSelect = ({ orderId, currentNoteId, currentLabel, onUpdate, notes: providedNotes }: OrderNoteSelectProps) => {
    const [loading, setLoading] = useState(false)
    const [created, setCreated] = useState<OrderNoteModel.OrderNote | null>(null)
    const { data: fetchedNotes } = useFetchOrderNotes({
        shouldFetchList: !providedNotes,
    })

    const listed = providedNotes || fetchedNotes
    const notes = created ? [created, ...listed.filter(n => n._id !== created._id)] : listed

    const handleUpdate = async (noteId: string) => {
        setLoading(true)
        const response = await OrderModel.updateNote({
            params: { id: orderId },
            body: { order_note_id: noteId },
        })

        if (response.success) {
            toast.success('Note updated successfully')
            onUpdate?.()
        } else {
            toast.error('Failed to update note')
        }
        setLoading(false)
    }

    const searchNotes = async (search: string) => {
        const response = await OrderNoteApi.getList({ query: { search, page: 1, length: 10 } })
        return response.success ? (response.data?.result ?? []) : []
    }

    const handleCreate = async (content: string) => {
        setLoading(true)
        const response = await OrderNoteApi.create({ body: { content } })
        const note: OrderNoteModel.OrderNote | undefined = response.data?.result

        if (response.success && note?._id) {
            toast.success(`Note "${content}" created`)
            setCreated(note)
            await handleUpdate(note._id)
        } else {
            toast.error(response.error?.data?.message || 'Failed to create note')
            setLoading(false)
        }
    }

    return (
        <SearchOrCreateCombobox
            items={notes}
            currentId={currentNoteId}
            currentLabel={currentLabel}
            getId={(note) => note._id}
            getLabel={(note) => note.content}
            search={searchNotes}
            onSelect={(note) => handleUpdate(note._id)}
            onCreate={handleCreate}
            placeholder="Select note"
            searchPlaceholder="Search or create a note"
            emptyText="No note found."
            busy={loading}
        />
    )
}
